import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ATTRIBUTION_MODELS, fetchReport, fetchSnippet, fetchCapiStatus, fetchCopilot,
  fetchRecommendations, askCopilot, fetchAlerts, fetchPaths,
} from "../../services/trackingService";
import ConnectPlatformBanner from "./components/ConnectPlatformBanner";

const BRAND = "#221C51";
const fmt = (n) => new Intl.NumberFormat("fr-FR").format(Math.round((Number(n) || 0) * 100) / 100);
const money = (n, c = "EUR") => `${fmt(n)} ${c === "EUR" ? "€" : c}`;

const StatTile = ({ label, value, sub, accent }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-3 md:p-4 min-w-0">
    <p className="text-[10px] md:text-[11px] uppercase font-bold tracking-wide text-gray-400 truncate">{label}</p>
    <p className="text-lg md:text-2xl font-bold mt-1 break-words" style={{ color: accent || BRAND }}>{value}</p>
    {sub && <p className="text-[10px] md:text-xs text-gray-400 mt-0.5 truncate">{sub}</p>}
  </div>
);

const CAPIStatus = () => {
  const [st, setSt] = useState(null);
  useEffect(() => { fetchCapiStatus().then(setSt).catch(() => {}); }, []);
  if (!st) return null;
  return (
    <div className="flex items-center gap-3 flex-wrap">
      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${st.mode === "auto" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
        CAPI · mode {st.mode}
      </span>
      {st.platforms.map((p) => (
        <span key={p.platform} className={`text-xs font-medium px-2 py-1 rounded-full ${p.configured ? "bg-green-50 text-green-700 border border-green-200" : "bg-gray-50 text-gray-400 border border-gray-200"}`}>
          {p.platform} {p.configured ? "✓" : "—"}
        </span>
      ))}
      <Link to="/dashboard/pixels" className="text-xs font-semibold text-[#221C51] underline">Gérer les pixels →</Link>
    </div>
  );
};

const SetupPanel = () => {
  const [snippet, setSnippet] = useState(null);
  useEffect(() => { fetchSnippet().then(setSnippet).catch(() => {}); }, []);
  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Copié !"); };
  if (!snippet) return null;
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-3 md:p-5">
      <h3 className="font-bold text-[#221C51] mb-1">Installation du pixel</h3>
      <p className="text-sm text-gray-400 mb-3">Collez ce snippet dans le &lt;head&gt; de votre site (ou via GTM Custom HTML).</p>
      <div className="relative">
        <pre className="text-[11px] bg-[#0F0B2D] text-green-300 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap">{snippet.install}</pre>
        <button onClick={() => copy(snippet.install)} className="absolute top-2 right-2 text-[10px] bg-white/10 text-white px-2 py-1 rounded hover:bg-white/20">Copier</button>
      </div>
      <div className="mt-3"><CAPIStatus /></div>
    </div>
  );
};

const TrackingDashboard = () => {
  const [report, setReport] = useState(null);
  const [insights, setInsights] = useState([]);
  const [recos, setRecos] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [paths, setPaths] = useState(null);
  const [loading, setLoading] = useState(true);
  const [model, setModel] = useState("last_click");
  const [groupBy, setGroupBy] = useState("campaign");
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [asking, setAsking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const spend = groupBy === "platform" ? { withSpend: 1 } : {};
    try {
      const [rep, cop, rec, al, pa] = await Promise.all([
        fetchReport({ model, groupBy, ...spend }),
        fetchCopilot({ model, groupBy, ...spend }).catch(() => null),
        fetchRecommendations({ model }).catch(() => null),
        fetchAlerts({ model }).catch(() => null),
        fetchPaths({ dimension: "platform" }).catch(() => null),
      ]);
      setReport(rep); setInsights(cop?.insights || []); setRecos(rec); setAlerts(al); setPaths(pa);
    } catch (e) { toast.error(e?.response?.data?.message || "Chargement du reporting impossible."); }
    finally { setLoading(false); }
  }, [model, groupBy]);

  const ask = async () => {
    if (!question.trim()) return;
    setAsking(true); setAnswer("");
    try { const r = await askCopilot(question.trim(), { model, groupBy }); setAnswer(r?.answer || "—"); }
    catch (e) { toast.error(e?.response?.data?.message || "Copilot indisponible."); }
    finally { setAsking(false); }
  };

  const SEV = { critical: "bg-red-50 border-red-200 text-red-700", good: "bg-green-50 border-green-200 text-green-700", warning: "bg-amber-50 border-amber-200 text-amber-700" };
  const ACT = { pause: "⏸ Mettre en pause", scale: "🚀 Scaler le budget", review: "🔍 À revoir" };

  useEffect(() => { load(); }, [load]);

  const t = report?.totals || { clicks: 0, conversions: 0, revenue: 0, cvr: 0, aov: 0, roas: 0, spend: 0 };

  return (
    <div className="p-2 md:p-4">
      <ConnectPlatformBanner moduleLabel="le tracking" accent="emerald" />
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-[#221C51]">Tracking &amp; Attribution</h2>
          <p className="text-sm text-gray-400 mt-0.5">Reporting server-side first-party — clics, conversions, revenu, ROAS.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link to="/dashboard/tracking-setup"
            className="text-sm px-3 py-2 rounded-lg border border-[#221C51]/25 text-[#221C51] font-semibold hover:bg-[#221C51]/5 transition whitespace-nowrap">
            Configurer
          </Link>
          <select value={model} onChange={(e) => setModel(e.target.value)} className="flex-1 md:flex-none min-w-0 text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:border-[#221C51]">
            {ATTRIBUTION_MODELS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            {["campaign", "platform"].map((g) => (
              <button key={g} onClick={() => setGroupBy(g)} className={`text-sm px-3 py-2 whitespace-nowrap ${groupBy === g ? "bg-[#221C51] text-white" : "bg-white text-gray-600"}`}>
                {g === "campaign" ? "Campagne" : "Plateforme"}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Totaux */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 mb-4">
        <StatTile label="Clics" value={fmt(t.clicks)} />
        <StatTile label="Conversions" value={fmt(t.conversions)} sub={`CVR ${fmt(t.cvr)}%`} accent="#0E9F6E" />
        <StatTile label="Revenu attribué" value={money(t.revenue)} sub={`AOV ${money(t.aov)}`} accent="#0E9F6E" />
        <StatTile label="ROAS" value={t.roas ? `${fmt(t.roas)}×` : "—"} sub={t.spend ? `Dépense ${money(t.spend)}` : "spend non connecté"} accent="#4F46E5" />
      </div>

      {/* Alertes automatiques */}
      {alerts?.alerts?.length > 0 && (
        <div className={`rounded-2xl p-3 md:p-4 mb-4 border ${alerts.counts.critical ? "bg-red-50 border-red-200" : "bg-amber-50 border-amber-200"}`}>
          <p className="text-sm font-bold mb-2">🔔 Alertes · {alerts.counts.critical} critiques · {alerts.counts.warning} à surveiller</p>
          <ul className="space-y-1">
            {alerts.alerts.slice(0, 6).map((a, i) => (
              <li key={i} className="text-sm flex items-start gap-2">
                <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${a.severity === "critical" ? "bg-red-500" : a.severity === "good" ? "bg-green-500" : "bg-amber-500"}`} />
                <span className="text-gray-700">{a.message}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Parcours de conversion (multi-touch) */}
      {paths?.paths?.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-3 md:p-4 mb-4">
          <div className="flex items-center justify-between mb-2 gap-2">
            <p className="text-sm font-bold text-[#221C51]">Parcours de conversion</p>
            <span className="text-xs font-semibold bg-sky-50 text-sky-700 px-2.5 py-1 rounded-full whitespace-nowrap">{paths.totals.multiTouchShare}% multi-touch</span>
          </div>
          <div className="space-y-2 md:space-y-1.5">
            {paths.paths.slice(0, 6).map((p, i) => (
              <div key={i} className="flex flex-col md:flex-row md:items-center md:justify-between text-sm gap-0.5 md:gap-0">
                <span className="text-gray-700 font-medium truncate">{p.label}</span>
                <span className="text-gray-500 tabular-nums text-xs md:text-sm md:flex-shrink-0 md:ml-3">{p.conversions} conv · {p.share}% · {money(p.revenue)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Copilot — insights + Q&A */}
      <div className="bg-purple-50 border border-purple-100 rounded-2xl p-3 md:p-4 mb-4">
        <p className="text-sm font-bold text-purple-800 mb-2">✨ Copilot</p>
        {insights.length > 0 && (
          <ul className="space-y-1.5 mb-3">
            {insights.map((s, i) => (
              <li key={i} className="text-sm text-gray-700 flex gap-2"><span className="text-purple-500">•</span>{s}</li>
            ))}
          </ul>
        )}
        <div className="flex gap-2">
          <input
            value={question} onChange={(e) => setQuestion(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()}
            placeholder="Posez une question : quelle campagne scaler ?"
            className="flex-1 min-w-0 px-3 py-2 text-sm border border-purple-200 rounded-lg focus:outline-none focus:border-purple-400 bg-white"
          />
          <button onClick={ask} disabled={asking || !question.trim()} className="flex-shrink-0 px-4 py-2 bg-purple-700 text-white text-sm font-semibold rounded-lg hover:bg-purple-800 disabled:opacity-50">
            {asking ? "…" : "Analyser"}
          </button>
        </div>
        {answer && <p className="mt-3 text-sm text-gray-700 bg-white rounded-lg p-3 whitespace-pre-wrap border border-purple-100">{answer}</p>}
      </div>

      {/* Recommandations d'optimisation */}
      {recos?.recommendations?.length > 0 && (
        <div className="mb-4">
          <p className="text-sm font-bold text-[#221C51] mb-2">Recommandations · {recos.counts.pause} à mettre en pause · {recos.counts.scale} à scaler</p>
          <div className="grid gap-2">
            {recos.recommendations.map((r, i) => (
              <div key={i} className={`rounded-xl border px-3 md:px-4 py-3 flex items-center justify-between ${SEV[r.severity] || "bg-gray-50 border-gray-200"}`}>
                <div>
                  <p className="text-sm font-bold">{ACT[r.action] || r.action} · <span className="text-gray-700">{r.name}</span></p>
                  <p className="text-xs opacity-80 mt-0.5">{r.reason}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <SetupPanel />

      {/* Tableau */}
      <div className="mt-4 bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                <th className="py-3 px-4">{groupBy === "campaign" ? "Campagne" : "Plateforme"}</th>
                <th className="py-3 px-4 text-right">Clics</th>
                <th className="py-3 px-4 text-right">Conv.</th>
                <th className="py-3 px-4 text-right">CVR</th>
                <th className="py-3 px-4 text-right">Revenu</th>
                <th className="py-3 px-4 text-right">AOV</th>
                <th className="py-3 px-4 text-right">ROAS</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="py-10 text-center text-gray-400">Chargement…</td></tr>
              ) : !report || report.rows.length === 0 ? (
                <tr><td colSpan={7} className="py-10 text-center text-gray-400">Aucune donnée de tracking pour le moment. Installez le pixel ci-dessus.</td></tr>
              ) : (
                report.rows.map((r) => (
                  <tr key={r.key} className="border-t border-gray-100">
                    <td className="py-3 px-4 font-semibold text-[#221C51]">{r.campaign_id || r.platform || r.key}</td>
                    <td className="py-3 px-4 text-right tabular-nums">{fmt(r.clicks)}</td>
                    <td className="py-3 px-4 text-right tabular-nums">{fmt(r.conversions)}</td>
                    <td className="py-3 px-4 text-right tabular-nums">{fmt(r.cvr)}%</td>
                    <td className="py-3 px-4 text-right tabular-nums font-semibold text-green-700">{money(r.revenue)}</td>
                    <td className="py-3 px-4 text-right tabular-nums">{money(r.aov)}</td>
                    <td className="py-3 px-4 text-right tabular-nums">{r.roas ? `${fmt(r.roas)}×` : "—"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TrackingDashboard;
